import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useModalRoot } from '../hooks/useModalRoot';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface CourseApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  closeOnEscape: boolean;
  closeOnOverlayClick: boolean;
  courseData: {
    id: string;
    title: string;
    date: string;
    price: string;
    instructor: string;
  } | null;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  consent?: string;
}

const CourseApplicationModal: React.FC<CourseApplicationModalProps> = ({
  isOpen,
  onClose,
  courseData,
  closeOnEscape,
  closeOnOverlayClick
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
    website: '',
    consent: false
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const modalRoot = useModalRoot();
  const modalRef = useRef<HTMLDivElement>(null);

  useFocusTrap(modalRef, isOpen);
  useBodyScrollLock(isOpen);


  // ACCESSIBILITY // 

  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);


  useEffect(() => {
    if (isOpen) {

      previousActiveElement.current = document.activeElement as HTMLElement | null;

      setTimeout(() => {
        if (modalRef.current) {

          const firstNameInput = modalRef.current.querySelector('#firstName') as HTMLElement | null;

          if (firstNameInput) {
            firstNameInput.focus();
          } else {

            const firstFocusable = modalRef.current.querySelector(
              'input, select, textarea, button, [href], [tabindex]:not([tabindex="-1"])'
            ) as HTMLElement | null;

            if (firstFocusable) {
              firstFocusable.focus();
            } else {
              modalRef.current.focus();
            }
          }
        }
      }, 0);
    } else {

      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    }
  }, [isOpen]);


  const handleOverlayClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && event.target === event.currentTarget) {
      onClose();
    }
  }, [closeOnOverlayClick, onClose]);

  // VALIDATION //

  const validateForm = (): boolean => {
    const fieldsToValidate = ['firstName', 'lastName', 'email', 'phone'] as const;

    const newErrors: FormErrors = {};

    fieldsToValidate.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    if (!formData.consent) {
      newErrors.consent = 'Du måste godkänna integritetspolicyn';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'firstName':
        if (!value.trim()) return 'Förnamn är obligatoriskt';
        if (value.trim().length < 2) return 'Förnamn måste vara minst 2 tecken';
        break;
      case 'lastName':
        if (!value.trim()) return 'Efternamn är obligatoriskt';
        if (value.trim().length < 2) return 'Efternamn måste vara minst 2 tecken';
        break;
      case 'email':
        if (!value.trim()) return 'E-post är obligatoriskt';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Ange en giltig e-postadress';
        break;
      case 'phone':
        if (!value.trim()) return 'Telefon är obligatoriskt';
        const phoneRegex = /^[\+]?[0-9\s\-\(\)]{7,}$/;
        if (!phoneRegex.test(value.replace(/\s/g, ''))) return 'Ange ett giltigt telefonnummer';
        break;
    }
    return undefined;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (name === 'consent') {
      setErrors(prev => ({
        ...prev,
        consent: undefined
      }));
    } else {
      const fieldError = validateField(name, value);
      setErrors(prev => ({
        ...prev,
        [name as keyof FormErrors]: fieldError
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!courseData) {
      console.error('Course data is not available');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/courseapplication/submitCourseApplication', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          courseInstanceId: courseData.id,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          website: formData.website // Honeypot field
        })
      });

      const result = await response.json();

      if (result.success) {
        alert(result.message);

        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          message: '',
          website: '',
          consent: false
        });
        setErrors({});
        onClose();
      } else {

        alert(result.message);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Ett fel uppstod. Försök igen senare.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // RENDERING //

  if (!isOpen || !courseData || !modalRoot) return null;

  return createPortal(
    <div
      ref={modalRef}
      className="modal show d-block"
      tabIndex={-1}
      onClick={handleOverlayClick}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="courseApplicationLabel"
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="courseApplicationLabel">Intresseanmälan - {courseData.title}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>

          <div className="modal-body">
            <div className="row mb-4 pb-3 border-bottom">
              <div className="col-md-4">
                <p><strong>Startdatum:</strong> {courseData.date}</p>
              </div>
              <div className="col-md-4">
                <p><strong>Instruktör:</strong> {courseData.instructor}</p>
              </div>
              <div className="col-md-4">
                <p><strong>Pris:</strong> {courseData.price}</p>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="courseFirstName" className="form-label">Förnamn *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                    id="courseFirstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                  {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="courseLastName" className="form-label">Efternamn *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                    id="courseLastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                  />
                  {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="courseEmail" className="form-label">E-post *</label>
                  <input
                    type="email"
                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                    id="courseEmail"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                  {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="coursePhone" className="form-label">Telefon *</label>
                  <input
                    type="tel"
                    className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                    id="coursePhone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                  {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="courseMessage" className="form-label">Meddelande</label>
                <textarea
                  className="form-control"
                  id="courseMessage"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleInputChange}
                ></textarea>
              </div>

              {/* Honeypot */}
              <div style={{ position: 'absolute', left: '-9999px', opacity: 0 }} aria-hidden="true">
                <label htmlFor="courseWebsite">Om du är en människa, lämna detta fältet tomt.</label>
                <input
                  type="text"
                  id="courseWebsite"
                  name="website"
                  value={formData.website || ''}
                  onChange={handleInputChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <div className="mb-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className={`form-check-input ${errors.consent ? 'is-invalid' : ''}`}
                    id="courseConsentCheckbox"
                    name="consent"
                    checked={formData.consent}
                    onChange={handleInputChange}
                    required
                  />
                  <label className="form-check-label" htmlFor="courseConsentCheckbox">
                    Jag godkänner att mina uppgifter sparas enligt{' '}
                    <a href="/integritetspolicy" target="_blank" rel="noopener noreferrer">
                      integritetspolicyn
                    </a>.
                  </label>
                  {errors.consent && <div className="invalid-feedback d-block">{errors.consent}</div>}
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2 pt-3 border-top">
                <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
                  Stäng
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Skickar...' : 'Skicka intresseanmälan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>,
    modalRoot
  );
};

export default CourseApplicationModal;